<script>
    import { onMount } from 'svelte';
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/ScrollTrigger';
    import { SplitText } from 'gsap/SplitText';

    gsap.registerPlugin(ScrollTrigger, SplitText);

    let name = $state('');
    let company = $state('');
    let email = $state('');
    let phone = $state('');
    let formSubmitted = $state(false);
    let errors = $state([]);
    let isSubmitting = $state(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    async function handleSubmit(e) {
        e.preventDefault();
        errors = [];

        if (name.length === 0) {
            errors.push('Please enter your name');
        }
        if (company.length === 0) {
            errors.push('Please enter the name of your company');
        }
        if (!emailRegex.test(email)) {
            errors.push('Please provide a valid email address');
        }

        if (typeof grecaptcha !== 'undefined') {
            const response = grecaptcha.getResponse();
            if (!response || response.length === 0) {
                errors.push('reCAPTCHA is required');
            }
        } else {
            errors.push('reCAPTCHA is not loaded');
        }

        if (errors.length === 0) {
            isSubmitting = true;
            const data = {
                EmailAddresses: 'events@xymogen.com',
                Subject: 'NEW: XX Sponsorship Form Submission',
                Body: 'The form was submitted on ' + new Date().toString() + ' with the following information:<br><br>' +
                    '<strong>Name</strong><br>' +
                    name + '<br><br>' +
                    '<strong>Company</strong><br>' +
                    company + '<br><br>' +
                    '<strong>Email</strong><br>' +
                    email + '<br><br>' +
                    (phone ? '<strong>Phone</strong><br>' + phone : ''),
                SentFromEmailAddress: 'no-reply@xymogen.com',
            };

            try {
                const response = await fetch(
                    'https://pqa-nopapi.wholescripts.com/api/sendemail',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(data),
                    }
                );
                if (response.ok) {
                    formSubmitted = true;
                } else {
                    errors.push('Something went wrong. Please try again.');
                }
            } catch (err) {
                errors.push('Something went wrong. Please try again.');
            } finally {
                isSubmitting = false;
            }
        }
    }

    let heroSubtitle;

    onMount(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!prefersReducedMotion && typeof window !== 'undefined') {
            if (document.fonts) {
                document.fonts.ready.then(initGSAP);
            } else {
                initGSAP();
            }
        }

        return () => {
            if (heroSubtitle) heroSubtitle.revert();
            ScrollTrigger.getAll().forEach(t => t.kill());
        };
    });

    function initGSAP() {
        // Dropdown functionality
        const dropdownButtons = document.querySelectorAll('.additional-opportunities-card-dropdown-button');
        dropdownButtons.forEach(button => {
            button.addEventListener('click', () => {
                const targetSelector = button.getAttribute('data-target');
                const action = button.getAttribute('data-action');
                const target = document.querySelector(targetSelector);
                const parentButtons = button.parentElement;

                if (target) {
                    if (action === 'expand') {
                        gsap.to(target, {
                            height: 'auto',
                            duration: 0.25,
                            ease: 'power1.inOut',
                            onStart: () => {
                                parentButtons.classList.add('is-open');
                            }
                        });
                    } else {
                        gsap.to(target, {
                            height: 0,
                            duration: 0.25,
                            ease: 'power1.inOut',
                            onComplete: () => {
                                parentButtons.classList.remove('is-open');
                            }
                        });
                    }
                }
            });
        });

        const mobileBreakpoint = window.getComputedStyle(document.documentElement).getPropertyValue('--large-mobile-breakpoint') || '768px';
        const mobileMediaQuery = window.matchMedia('(max-width: ' + mobileBreakpoint + ')');

        if (mobileMediaQuery.matches) {
            gsap.from('.sponsor-hero-imgs--mobile .border-img', {
                autoAlpha: 0,
                y: -50,
                duration: 0.75,
                stagger: {
                    amount: 0.5,
                    from: 'random',
                },
            });
        } else {
            gsap.from('.alt-hero .sponsor-hero-imgs-1 .border-img', {
                autoAlpha: 0,
                x: -50,
                duration: 0.75,
                stagger: {
                    amount: 0.5,
                    from: 'random',
                },
            });

            gsap.from('.alt-hero .sponsor-hero-imgs-2 .border-img', {
                autoAlpha: 0,
                x: 50,
                duration: 0.75,
                stagger: {
                    amount: 0.5,
                    from: 'random',
                },
                delay: 0.25,
            });
        }

        gsap.from('.alt-hero-title', {
            yPercent: 100,
            autoAlpha: 0,
            ease: 'power2.out',
        });

        heroSubtitle = new SplitText('.alt-hero-subtitle', {
            type: 'lines',
            linesClass: 'line',
        });

        // Apply overflow hidden inline — only needed during the reveal
        heroSubtitle.lines.forEach(line => {
            line.style.overflow = 'hidden';
            line.style.display = 'block';
        });

        gsap.from(heroSubtitle.lines, {
            yPercent: 100,
            autoAlpha: 0,
            ease: 'power2.out',
            stagger: 0.1,
            onComplete: () => {
                heroSubtitle.lines.forEach(line => line.style.overflow = '');
            },
        });

        gsap.from('.sponsor-tier-card', {
            scrollTrigger: {
                trigger: '.sponsor-tier-cards-container',
                start: 'top 80%',
            },
            autoAlpha: 0,
            y: 50,
            duration: 0.75,
            stagger: 0.2,
        });

        gsap.from('.additional-sponsorships', {
            scrollTrigger: {
                trigger: '.additional-sponsorships',
                start: 'top 80%',
            },
            autoAlpha: 0,
            y: 50,
            duration: 0.75,
            stagger: 0.2,
        });
    }

</script>

<svelte:head>
    <title>Sponsorships | XYMOGEN XPERIENCE 2026</title>
    <script src="https://www.google.com/recaptcha/api.js" async defer></script>
</svelte:head>

<section class="alt-hero section-block-margin">
    <div class="alt-hero__content">
        <h1 class="alt-hero-title">Sponsorships</h1>
        <h2 class="heading-5 font-text alt-hero-subtitle">
            Join us for the XYMOGEN XPERIENCE, where industry leaders converge to explore the future of integrative and functional medicine.
        </h2>
        <div class="sponsor-hero-imgs-1">
            <img src="/media/xx26-about-image1.webp" alt="" class="border-img sponsor-hero-img--3">
            <img src="/media/xx26-sponsorship-hero2.webp" alt="" class="border-img sponsor-hero-img--2">
            <img src="/media/xx26-sponsorship-hero1.webp" alt="" class="border-img sponsor-hero-img--1">
        </div>
        <div class="sponsor-hero-imgs-2">
            <img src="/media/xx26-about-image9.webp" alt="" class="border-img sponsor-hero-img--4">
            <img src="/media/xx26-sponsorship-hero5.webp" alt="" class="border-img sponsor-hero-img--5">
            <img src="/media/xx26-sponsorship-hero6.webp" alt="" class="border-img sponsor-hero-img--6">
        </div>
        <div class="sponsor-hero-imgs--mobile">
            <img src="/media/xx26-about-image9.webp" alt="" class="border-img sponsor-hero-img--4">
            <img src="/media/xx26-sponsorship-hero2.webp" alt="" class="border-img sponsor-hero-img--2">
            <img src="/media/xx26-sponsorship-hero6.webp" alt="" class="border-img sponsor-hero-img--6">
        </div>
    </div>
</section>

<section class="sponsorship-interest section-block-margin page-container">
    <div class="sponsorship-interest__content">
        <div class="sponsorship-interest__form-area">
            <div class="block-3" aria-hidden="true"></div>
            <div class="sponsorship-interest__form-container">
                {#if !formSubmitted}
                    <h2 class="heading-3 font-text text-gray-900 text-600 mb-5">Interested in becoming a sponsor for our next XPERIENCE?</h2>
                    <p class="sponsorship-interest__intro mb-10">
                        Fill out this form below or contact us at <a class="text-link" href="mailto:events@xymogen.com">events@xymogen.com</a> for sponsor opportunities.
                    </p>
                    <form onsubmit={handleSubmit} novalidate>
                        <p class="mb-4">*Required field</p>
                        <div class="form-group-2">
                            <label>
                                <span class="input-label">Your Name*</span>
                                <input bind:value={name} class="input-text input--lg" class:input-text--error={errors.includes('Please enter your name')} required />
                                {#if errors.includes('Please enter your name')}
                                    <span class="text-sm text-error-700 mt-1 input-error-hint">Please enter your name</span>
                                {/if}
                            </label>
                            <label>
                                <span class="input-label">Company Name*</span>
                                <input bind:value={company} class="input-text input--lg" class:input-text--error={errors.includes('Please enter the name of your company')} required />
                                {#if errors.includes('Please enter the name of your company')}
                                    <span class="text-sm text-error-700 mt-1 input-error-hint">Please enter the name of your company</span>
                                {/if}
                            </label>
                        </div>
                        <div class="form-group-2">
                            <label>
                                <span class="input-label">Email*</span>
                                <input bind:value={email} type="email" class="input-text input--lg" class:input-text--error={errors.includes('Please provide a valid email address')} required />
                                {#if errors.includes('Please provide a valid email address')}
                                    <span class="text-sm text-error-700 mt-1 input-error-hint">Please provide a valid email address</span>
                                {/if}
                            </label>
                            <label>
                                <span class="input-label">Phone Number</span>
                                <input bind:value={phone} type="tel" class="input-text input--lg" />
                            </label>
                        </div>
                        <div class="sponsor-form-captcha-container">
                            <div class="g-recaptcha" data-sitekey="6LfbpicsAAAAAGoTvoCW7jK2mj4tpTTwC5Rpp4_-"></div>
                        </div>
                        {#if errors.length > 0}
                            <ul class="error-list mb-3">
                                {#each errors as error}
                                    <li>{error}</li>
                                {/each}
                            </ul>
                        {/if}
                        <div class="text-right">
                            <button class="btn btn-lg btn-primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Sending...' : 'Get in Touch'}
                            </button>
                        </div>
                    </form>
                {:else}
                    <div id="sponsorFormSuccessDIV">
                        <h3 class="heading-5 font-text text-gray-900 text-600 mb-2">Thank you for your interest</h3>
                        <p>We will contact you as soon as we review your information.</p>
                    </div>
                {/if}
            </div>
        </div>
        <img class="sponsor-form-img" src="/media/sponsorship-form.png" alt="XYMOGEN event sponsorship display">
    </div>
</section>

<!--<section class="section-block-margin">-->
<!--    <div class="page-container gradient-bg">-->
<!--        <div class="section-intro-row">-->
<!--            <div class="section__intro">-->
<!--                <div>-->
<!--                    <h2 class="heading-3 font-text text-400 text-gray-700 mb-2">Floor Plan</h2>-->
<!--                    <div class="block-2"></div>-->
<!--                </div>-->
<!--            </div>-->

<!--            <a class="btn btn-lg btn-primary" href="/media/xx26-sponsorships-floorplan-100826.pdf" target="_blank">-->
<!--                Download Floor Plan-->
<!--                <i class="btn__indicator fa-sharp fa-solid fa-download"></i>-->
<!--            </a>-->
<!--        </div>-->
<!--        <div class="content-container">-->
<!--            <img class="floorplan-img" src="/media/xx26-floorplan-100826.webp" alt="Floor plan, stage and seating layout, and sponsor booth arrangement for the 2026 Xymogen Experience"/>-->
<!--        </div>-->
<!--    </div>-->
<!--</section>-->

<!--<section class="section-block-margin">-->
<!--    <div class="page-container sponsor-tiers-section gradient-bg">-->
<!--        <div class="section__intro">-->
<!--            <div>-->
<!--                <h2 class="heading-3 font-text text-400 text-gray-700 mb-2">Sponsorship Opportunities</h2>-->
<!--                <div class="block-2"></div>-->
<!--            </div>-->
<!--        </div>-->
<!--        <section class="sponsor-tier-cards-container content-container">-->
<!--            <div class="sponsor-tier-card &#45;&#45;platinum">-->
<!--                <div class="sold-out"><span>SOLD OUT</span></div>-->
<!--                <img src="/media/xx26-sponsorship-platinum-icon.png" alt="" class="sponsor-tier-card__icon">-->
<!--                <div class="sponsor-tier-card__header">-->
<!--                    <h3 class="heading-4 font-text text-600 text-gray-900">Platinum</h3>-->
<!--                </div>-->
<!--                <div class="text-sm text-gray-900 text-600">CORE PERKS</div>-->
<!--                <ul>-->
<!--                    <li>Breakfast Presentation</li>-->
<!--                    <li>10' × 10' Premium Booth Location</li>-->
<!--                    <li>Two (2) Complimentary Attendee Passes</li>-->
<!--                    <li>Two (2) Exhibitor Badges</li>-->
<!--                    <li>8.5" × 11" Bag Insert + One (1) Product Sample</li>-->
<!--                    <li>36" × 80" Banner Stand Placement</li>-->
<!--                    <li>Inclusion in “Meet Our Sponsors” Dedicated Email</li>-->
<!--                    <li>Social Media Recognition</li>-->
<!--                    <li>8‑Week Digital Ad on WholeScripts.com</li>-->
<!--                    <li>Main Stage Intermission Slides</li>-->
<!--                </ul>-->

<!--                <p class="heading-4 font-text text-center text-600 text-gray-900 mt-6">$20,000</p>-->
<!--            </div>-->
<!--            <div class="sponsor-tier-card &#45;&#45;gold">-->
<!--                <div class="sold-out"><span>SOLD OUT</span></div>-->
<!--                <img src="/media/xx26-sponsorship-gold-icon.png" alt="" class="sponsor-tier-card__icon">-->
<!--                <div class="sponsor-tier-card__header">-->
<!--                    <h3 class="heading-4 font-text text-600 text-gray-900">Gold</h3>-->
<!--                </div>-->
<!--                <ul>-->
<!--                    <li>8' × 10' Booth Space</li>-->
<!--                    <li>One (1) Complimentary Attendee Pass</li>-->
<!--                    <li>Two (2) Exhibitor Badges</li>-->
<!--                    <li>8.5" × 11" Bag Insert</li>-->
<!--                    <li>Inclusion in “Meet Our Sponsors” Dedicated Email</li>-->
<!--                    <li>Social Media Recognition</li>-->
<!--                    <li>4‑Week Digital Ad on WholeScripts</li>-->
<!--                </ul>-->
<!--                <p class="heading-4 font-text text-center text-600 text-gray-900 mt-6">$10,000</p>-->
<!--            </div>-->
<!--            <div class="sponsor-tier-card &#45;&#45;silver">-->
<!--                <div class="sold-out"><span>SOLD OUT</span></div>-->
<!--                <img src="/media/xx26-sponsorship-silver-icon.png" alt="" class="sponsor-tier-card__icon">-->
<!--                <div class="sponsor-tier-card__header">-->
<!--                    <h3 class="heading-4 font-text text-600 text-gray-900">Silver</h3>-->
<!--                </div>-->
<!--                <ul>-->
<!--                    <li>Five (5) Complimentary Virtual Tickets</li>-->
<!--                    <li>8.5" × 11" Bag Insert</li>-->
<!--                    <li>Inclusion in “Meet Our Sponsors” Dedicated Email</li>-->
<!--                    <li>Social Media Recognition</li>-->
<!--                </ul>-->
<!--                <p class="heading-4 font-text text-center text-600 text-gray-900 mt-6">$5,000</p>-->
<!--            </div>-->
<!--        </section>-->

<!--        <section class="additional">-->
<!--            <h3 class="heading-5 font-text text-600 text-gray-700 text-center">Additional Sponsorship Opportunities</h3>-->
<!--            <div class="additional-opportunities-card">-->
<!--                <div class="additional-opportunities-card-inner">-->
<!--                <div class="sold-out"><span>SOLD OUT</span></div>-->
<!--                    <div class="additional-opportunities-card-header">-->
<!--                        <span>Item</span>-->
<!--                        <span>Price</span>-->
<!--                    </div>-->
<!--                    <div class="additional-opportunities-card-row">-->
<!--                        <span>Beverage Service</span>-->
<!--                        <span>$5,000</span>-->
<!--                    </div>-->
<!--                    <div class="additional-opportunities-card-row">-->
<!--                        <span>Wi-Fi Sponsor</span>-->
<!--                        <span>$5,000</span>-->
<!--                    </div>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-row has-dropdown">-->
<!--                    <span>Welcome Reception Sponsor (Friday)</span>-->
<!--                    <span>$7,500</span>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-dropdown" id="reception">-->
<!--                    <ul>-->
<!--                        <li>8x10 Booth Space</li>-->
<!--                        <li>Two (2) Exhibitor Badges</li>-->
<!--                        <li>8.5” x 11” Bag Insert</li>-->
<!--                        <li>Main Stage Intermission slides</li>-->
<!--                        <li>Sponsor Highlight during event</li>-->
<!--                        <li>Branded cocktail napkins</li>-->
<!--                    </ul>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-buttons">-->
<!--                    <button class="additional-opportunities-card-dropdown-button" data-target="#reception" data-action="expand">Expand details</button>-->
<!--                    <button class="additional-opportunities-card-dropdown-button" data-target="#reception" data-action="collapse">Collapse details</button>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-row has-dropdown">-->
<!--                    <span>Cocktail Hour Sponsor (Saturday)</span>-->
<!--                    <span>$7,500</span>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-dropdown" id="cocktail">-->
<!--                    <ul>-->
<!--                        <li>8x10 Booth Space</li>-->
<!--                        <li>Two (2) Exhibitor Badges</li>-->
<!--                        <li>8.5” x 11” Bag Insert</li>-->
<!--                        <li>Main Stage Intermission slides</li>-->
<!--                        <li>Sponsor Highlight during event</li>-->
<!--                        <li>Branded cocktail napkins</li>-->
<!--                    </ul>-->
<!--                </div>-->
<!--                <div class="additional-opportunities-card-buttons">-->
<!--                    <button class="additional-opportunities-card-dropdown-button" data-target="#cocktail" data-action="expand">Expand details</button>-->
<!--                    <button class="additional-opportunities-card-dropdown-button" data-target="#cocktail" data-action="collapse">Collapse details</button>-->
<!--                </div>-->
<!--            </div>-->
<!--        </section>-->
<!--    </div>-->
<!--</section>-->


<!--<section class="section-block-margin page-container content-container sponsor-form-container">-->
<!--    <div class="sponsor-form-area">-->
<!--        <div class="block-3"></div>-->
<!--        <div class="sponsor-form-area__form-container">-->
<!--            {#if !formSubmitted}-->
<!--                <h2 class="heading-3 font-text text-gray-900 text-600 mb-5">Interested in becoming a sponsor?</h2>-->
<!--                <p class="text-gray-700 mb-10">-->
<!--                    Fill out this form below or contact us at <a class="text-link" href="mailto:events@xymogen.com">events@xymogen.com</a> for sponsor opportunities.-->
<!--                </p>-->
<!--                <form onsubmit={handleSubmit} novalidate>-->
<!--                    <p class="mb-4">*Required field</p>-->
<!--                    <div class="form-group-2">-->
<!--                        <label>-->
<!--                            <div class="input-label">YOUR NAME*</div>-->
<!--                            <input bind:value={name} class="input-text input-lg" class:input-text&#45;&#45;error={errors.includes("Please enter your name")} required />-->
<!--                            {#if errors.includes("Please enter your name")}-->
<!--                                <div class="text-sm text-error-700 mt-1 input-error-hint">Please enter your name</div>-->
<!--                            {/if}-->
<!--                        </label>-->
<!--                        <label>-->
<!--                            <div class="input-label">COMPANY NAME*</div>-->
<!--                            <input bind:value={company} class="input-text input-lg" class:input-text&#45;&#45;error={errors.includes("Please enter the name of your company")} required />-->
<!--                            {#if errors.includes("Please enter the name of your company")}-->
<!--                                <div class="text-sm text-error-700 mt-1 input-error-hint">Please enter the name of your company</div>-->
<!--                            {/if}-->
<!--                        </label>-->
<!--                    </div>-->
<!--                    <div class="form-group-2">-->
<!--                        <label>-->
<!--                            <div class="input-label">EMAIL*</div>-->
<!--                            <input bind:value={email} type="email" class="input-text input-lg" class:input-text&#45;&#45;error={errors.includes("Please provide a valid email address")} required />-->
<!--                            {#if errors.includes("Please provide a valid email address")}-->
<!--                                <div class="text-sm text-error-700 mt-1 input-error-hint">Please provide a valid email address</div>-->
<!--                            {/if}-->
<!--                        </label>-->
<!--                        <label>-->
<!--                            <div class="input-label">PHONE NUMBER</div>-->
<!--                            <input bind:value={phone} type="tel" class="input-text input-lg" />-->
<!--                        </label>-->
<!--                    </div>-->
<!--                    <div class="sponsor-form-captcha-container">-->
<!--                        <div class="g-recaptcha" data-sitekey="6LfbpicsAAAAAGoTvoCW7jK2mj4tpTTwC5Rpp4_-"></div>-->
<!--                    </div>-->
<!--                    {#if errors.length > 0}-->
<!--                        <ul class="error-list mb-3">-->
<!--                            {#each errors as error}-->
<!--                                <li>{error}</li>-->
<!--                            {/each}-->
<!--                        </ul>-->
<!--                    {/if}-->
<!--                    <div class="text-right">-->
<!--                        <button class="btn btn-lg btn-primary" type="submit" disabled={isSubmitting}>-->
<!--                            {isSubmitting ? 'Sending...' : 'Get in Touch'}-->
<!--                        </button>-->
<!--                    </div>-->
<!--                </form>-->
<!--            {:else}-->
<!--                <div id="sponsorFormSuccessDIV">-->
<!--                    <h3 class="heading-5 font-text text-gray-900 text-600 mb-2">-->
<!--                        <i class="fa-sharp fa-solid fa-paper-plane mr-4"></i>-->
<!--                        Thank you for your interest-->
<!--                    </h3>-->
<!--                    <p>We will contact you as soon as we review your information.</p>-->
<!--                </div>-->
<!--            {/if}-->
<!--        </div>-->
<!--    </div>-->
<!--    <img class="sponsor-form-img" src="/media/xx26-sponsorship-form-final.webp" alt="">-->
<!--</section>-->



